import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isTextColorRole } from '../tokens';
import type { TextColorRole } from '../tokens';
import { hexColorSwatchValue } from '../hex-color';
import { HexColorError, useHexColorInput } from './use-hex-color-input';
import { InheritCustomToggle } from './inherit-custom-toggle';
import {
  FIELD_CONTROL_FONT_SIZE,
  FIELD_CONTROL_PADDING,
  FIELD_CONTROL_RADIUS,
  fieldControlStyle,
  selectControlStyle,
  disabledSelectControlStyle,
} from './field-control-style';

type InheritColorRoleFieldProps = {
  field: { label?: string };
  name: string;
  /** Undefined when the prop is absent from saved page data; that reads as Inherit. */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  readOnly?: boolean;
};

const ROLE_OPTIONS: readonly { label: string; value: TextColorRole }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
  { label: 'Link', value: 'link' },
];

const CUSTOM_OPTION_VALUE = 'custom';

/** Starting color when the author switches from Inherit to Custom. */
const CUSTOM_DEFAULT = '#121516';

const dropdownStyle: React.CSSProperties = {
  ...selectControlStyle,
  padding: `var(--dcm-cms-color-role-select-padding, ${FIELD_CONTROL_PADDING})`,
  paddingRight: selectControlStyle.paddingRight,
  borderColor:
    'var(--dcm-cms-color-role-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))',
  borderRadius: `var(--dcm-cms-color-role-border-radius, ${FIELD_CONTROL_RADIUS})`,
  fontSize: `var(--dcm-cms-color-role-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
  color: 'var(--dcm-cms-color-role-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))',
  backgroundColor: 'var(--dcm-cms-color-role-bg, var(--puck-field-color-bg, #fff))',
};

/**
 * Inherit / Custom control for a text color whose element has a default theme role.
 *
 * Inherit means "the element's default role" (`inheritRole`), stored as that role key, so saved
 * data and rendering are unchanged and nothing needs migrating. Custom shows a dropdown of the other
 * roles plus "Custom" (a hex). Starting Custom from Inherit seeds a hex, so the dropdown reads
 * "Custom" rather than silently picking a role. The default role is not offered in the Custom
 * dropdown, because choosing it is the same thing as Inherit.
 *
 * Pass `null` for a color that has no default role of its own and instead follows another prop when
 * absent (FAQ Heading color follows Question color). There Inherit means "the prop is absent": it
 * reads as Inherit only when nothing is stored, switching back to Inherit unsets the prop (so the
 * fallback keeps applying), and every role is offered under Custom, including an explicit 'text'.
 */
export function createInheritColorRoleField(
  inheritRole: TextColorRole | null
): (props: InheritColorRoleFieldProps) => JSX.Element {
  return function InheritColorRoleField({
    field,
    name,
    value,
    onChange,
    readOnly,
  }: InheritColorRoleFieldProps): JSX.Element {
    const current = value ?? '';
    const isInherit = current === '' || (inheritRole !== null && current === inheritRole);
    const isHex = !isInherit && !isTextColorRole(current);
    const hexInput = useHexColorInput(current, onChange);
    const labelText = field.label ?? name;

    const handleModeChange = useCallback(
      (mode: 'custom' | 'inherit') => {
        if (mode === 'inherit') onChange(inheritRole ?? undefined);
        else if (isInherit) onChange(CUSTOM_DEFAULT);
      },
      [isInherit, onChange]
    );

    const handleSelectChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const chosen = e.currentTarget.value;
        if (chosen === CUSTOM_OPTION_VALUE) {
          if (!isHex) onChange(CUSTOM_DEFAULT);
        } else {
          onChange(chosen);
        }
      },
      [isHex, onChange]
    );

    return (
      <FieldLabel label={labelText} readOnly={readOnly} el='div'>
        <InheritCustomToggle
          label={labelText}
          name={name}
          testIdPrefix='inherit-color-mode'
          isInherit={isInherit}
          onSelect={handleModeChange}
          readOnly={readOnly}
        />
        {!isInherit && (
          <div style={{ marginTop: 8 }}>
            <select
              value={isHex ? CUSTOM_OPTION_VALUE : current}
              onChange={handleSelectChange}
              disabled={readOnly}
              aria-label={`${labelText} color role`}
              data-testid={`inherit-color-select-${name}`}
              style={{ ...dropdownStyle, ...(readOnly && disabledSelectControlStyle) }}
            >
              {ROLE_OPTIONS.filter((opt) => opt.value !== inheritRole).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE}>Custom</option>
            </select>
          </div>
        )}
        {isHex && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type='color'
              value={hexColorSwatchValue(current, CUSTOM_DEFAULT)}
              onChange={hexInput.handleSwatchChange}
              disabled={readOnly}
              aria-label={`${labelText} custom color swatch`}
              data-testid={`inherit-color-swatch-${name}`}
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                padding: 0,
                border: '1px solid var(--dcm-cms-color-role-border-color, #d0d4d8)',
                borderRadius: 'var(--dcm-cms-color-role-border-radius, 4px)',
                cursor: 'pointer',
              }}
            />
            <input
              type='text'
              value={hexInput.text}
              onChange={hexInput.handleChange}
              disabled={readOnly}
              aria-label={`${labelText} custom color value`}
              aria-invalid={hexInput.invalid || undefined}
              aria-describedby={hexInput.invalid ? hexInput.errorId : undefined}
              data-testid={`inherit-color-hex-${name}`}
              style={{
                ...fieldControlStyle,
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--dcm-cms-color-role-font-family, monospace)',
              }}
            />
          </div>
        )}
        {isHex && hexInput.invalid && <HexColorError id={hexInput.errorId} />}
      </FieldLabel>
    );
  };
}
